// AI assisted development
/**
 * DevAgent Pipeline Hooks
 * 
 * Custom hooks for managing pipeline data with TanStack Query
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CreatePipelineRequest,
  PipelineListFilters,
  ApproveStageRequest,
  AssignDeveloperRequest,
  AssignDeveloperResponse,
  ResolveRequirementGapRequest,
  ResolveRequirementGapResponse,
  StartImplementationResponse,
  ConfirmManualImplementationResponse,
  ApproveDocumentPackageRequest,
  ApproveDocumentPackageResponse,
  RemoveRepositoryRequest,
  RemoveRepositoryResponse,
  ChangeRepositoriesRequest,
  ChangeRepositoriesResponse,
  PipelineFacets,
  Pipeline,
  ListPipelinesResponse,
  AnalyticsDateRange,
  AnalyticsTimeRange,
  DocumentListResponse,
  DocumentSetResponse,
  RepositoryWorkspaceResponse,
} from '../types/devagent';
import * as devagentApi from '../services/devagentApi';
import {
  getEffectivePipelineStatus,
  isPrMonitoringElapsedReason,
} from '../utils/pipelineStatus';

// Query keys
export const devagentKeys = {
  all: ['devagent'] as const,
  pipelines: () => [...devagentKeys.all, 'pipelines'] as const,
  pipelinesList: (filters?: PipelineListFilters) => [...devagentKeys.pipelines(), 'list', filters] as const,
  pipelinesFacets: (filters?: PipelineListFilters) => [...devagentKeys.all, 'facets', filters] as const,
  pipeline: (id: string) => [...devagentKeys.pipelines(), 'detail', id] as const,
  pipelineAsk: (id: string) => [...devagentKeys.pipeline(id), 'ask'] as const,
  pipelineDocuments: (id: string) => [...devagentKeys.pipeline(id), 'documents'] as const,
  repositoryWorkspace: (id: string, repositoryId: number) => [...devagentKeys.pipeline(id), 'repository-workspace', repositoryId] as const,
  documentReviewPending: (id: string) => [...devagentKeys.pipelineDocuments(id), 'review-pending'] as const,
  capabilities: () => [...devagentKeys.all, 'capabilities'] as const,
  stats: () => [...devagentKeys.all, 'stats'] as const,
  analytics: (filters?: devagentApi.AnalyticsFilters) => [...devagentKeys.all, 'analytics', filters] as const,
  repoSelectionAccuracy: (range: AnalyticsDateRange | AnalyticsTimeRange) => [...devagentKeys.all, 'repo-selection-accuracy', range] as const,
  repoOverSelection: (range: AnalyticsDateRange | AnalyticsTimeRange) => [...devagentKeys.all, 'repo-over-selection', range] as const,
};

// ---------------------------------------------------------------------------
// T023 – Adaptive polling helpers
// ---------------------------------------------------------------------------

/** Subscribe to document.visibilityState changes. */
function useDocumentVisibility(): boolean {
  const [visible, setVisible] = React.useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );

  React.useEffect(() => {
    const handleChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleChange);
    return () => document.removeEventListener('visibilitychange', handleChange);
  }, []);

  return visible;
}

/** Track user activity; returns false after idleThresholdMs of inactivity. */
function useActivityDetector(idleThresholdMs = 30_000): boolean {
  const [active, setActive] = React.useState(true);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const reset = () => {
      setActive(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setActive(false), idleThresholdMs);
    };

    reset(); // start timer immediately

    const events = ['mousemove', 'keydown', 'scroll', 'pointerdown', 'touchstart'];
    events.forEach(e => document.addEventListener(e, reset, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(e => document.removeEventListener(e, reset));
    };
  }, [idleThresholdMs]);

  return active;
}

export const LIST_POLL_ACTIVE_MS = 15_000;
export const LIST_POLL_IDLE_MS = 30_000;
export const LIST_POLL_HIDDEN_MS = 60_000;
export const DETAIL_POLL_ACTIVE_MS = 3_000;
export const SECONDARY_FACET_POLL_MS = 60_000;
const LIST_QUERY_MAX_RETRIES = 2;
const DETAIL_QUERY_MAX_RETRIES = 4;

/**
 * Compute adaptive refetch interval based on page visibility and user activity.
 *   - visible + active  → 15 s (list/facets)
 *   - visible + idle    → 30 s
 *   - hidden            → 60 s
 */
function useAdaptiveRefetchInterval(
  activeMs = LIST_POLL_ACTIVE_MS,
  idleMs = LIST_POLL_IDLE_MS,
  hiddenMs = LIST_POLL_HIDDEN_MS,
): number {
  const visible = useDocumentVisibility();
  const active = useActivityDetector(30_000);

  if (!visible) return hiddenMs;
  if (!active) return idleMs;
  return activeMs;
}

export type DocumentReviewPendingMap = Record<string, boolean>;

const TERMINAL_DETAIL_POLL_STATES = new Set([
  'failed',
  'completed',
  'cancelled',
  'deleted',
  'abandoned',
  'retired',
  'pr_merged',
  'deployed_qa',
  'deployed_prod',
]);

const ACTIVE_DETAIL_POLL_STATES = new Set([
  'pending',
  'running',
  'in_progress',
  'defect_investigation',
]);

export function pipelineDetailRefetchInterval(
  pipeline: Pipeline | undefined,
  activeIntervalMs = 3_000,
): number | false {
  if (!pipeline) return false;

  const effectiveStatus = getEffectivePipelineStatus(pipeline).toLowerCase();
  if (pipeline.queue_status && !['completed', 'cancelled', 'deleted', 'retired', 'pr_merged', 'deployed_qa', 'deployed_prod'].includes(effectiveStatus)) return activeIntervalMs;
  if (TERMINAL_DETAIL_POLL_STATES.has(effectiveStatus)) return false;
  if (pipeline.current_stage === 'documents_snapshotting') return activeIntervalMs;
  if (ACTIVE_DETAIL_POLL_STATES.has(effectiveStatus)) return activeIntervalMs;
  return 30_000;
}

/**
 * Keep pipeline-detail reads retrying when the service is busy, while allowing
 * permanent client errors (for example, a confirmed 404) to reach the UI.
 */
export function isPipelineDetailRetryableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return true;

  const errorWithStatus = error as {
    status?: unknown;
    response?: { status?: unknown };
  };
  const rawStatus = errorWithStatus.status ?? errorWithStatus.response?.status;
  if (typeof rawStatus !== 'number') return true;

  return rawStatus === 0 || rawStatus === 408 || rawStatus === 425 || rawStatus === 429 || rawStatus >= 500;
}

export function pipelineDetailRetryDelay(attemptIndex: number): number {
  return Math.min(5_000 * (2 ** attemptIndex), 15_000);
}

type PipelineDocumentsQueryData = {
  metadata: DocumentSetResponse | null;
  listing: DocumentListResponse | null;
};

export function pipelineDocumentsRefetchInterval(
  documentPackage: PipelineDocumentsQueryData | null | undefined,
): number | false {
  const metadataStatus = documentPackage?.metadata?.status?.toLowerCase();
  const listingStatus = documentPackage?.listing?.status?.toLowerCase();
  const reviewActive = Object.values(documentPackage?.listing?.advisory_reviews ?? {})
    .some(review => review.state === 'pending' || review.state === 'running');
  const legacyReviewState = documentPackage?.listing?.advisory_review?.state;
  return metadataStatus === 'generating' || listingStatus === 'generating' || reviewActive
    || legacyReviewState === 'pending' || legacyReviewState === 'running'
    ? 10_000
    : false;
}

export function setDocumentReviewPending(
  queryClient: QueryClient,
  pipelineId: string,
  documentName: string,
  isPending: boolean,
) {
  queryClient.setQueryData<DocumentReviewPendingMap>(
    devagentKeys.documentReviewPending(pipelineId),
    (current = {}) => {
      if (isPending) {
        return { ...current, [documentName]: true };
      }
      const next = { ...current };
      delete next[documentName];
      return next;
    },
  );
}

export function useDocumentReviewPending(pipelineId: string) {
  return useQuery<DocumentReviewPendingMap>({
    queryKey: devagentKeys.documentReviewPending(pipelineId),
    queryFn: () => ({}),
    enabled: Boolean(pipelineId),
    initialData: {},
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });
}

export function useDevAgentCapabilities() {
  return useQuery({
    queryKey: devagentKeys.capabilities(),
    queryFn: devagentApi.getCapabilities,
    staleTime: 60000,
    refetchInterval: 60000,
  });
}

// ---------------------------------------------------------------------------
// T024 – Facets hook
// ---------------------------------------------------------------------------

/**
 * Fetch aggregated facet counts.
 * Polls at the same adaptive interval as the pipeline list.
 */
export function useDevAgentPipelineFacets(
  filters?: PipelineListFilters,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const interval = useAdaptiveRefetchInterval();
  const enabled = options?.enabled ?? true;
  const refetchInterval = options?.refetchInterval ?? interval;

  return useQuery<PipelineFacets>({
    queryKey: devagentKeys.pipelinesFacets(filters),
    queryFn: () => devagentApi.getPipelineFacets(filters),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    staleTime: Math.min(10_000, refetchInterval),
    retry: (failureCount, error) => {
      if (error instanceof devagentApi.DevAgentApiError && error.status === 304) return false;
      if (!isPipelineDetailRetryableError(error)) return false;
      return failureCount < LIST_QUERY_MAX_RETRIES;
    },
  });
}

// ---------------------------------------------------------------------------
// T025 – Mark-seen mutation hook
// ---------------------------------------------------------------------------

/**
 * Mutation hook to mark pipelines as seen for the current user.
 * Fires on page mount and window focus from consuming components.
 */
export function useMarkPipelinesSeen() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string[] | undefined>({
    mutationFn: (pipelineIds?: string[]) => devagentApi.markPipelinesSeen(pipelineIds),
    onSuccess: () => {
      // Invalidate so has_changes_since_last_seen is refreshed on next poll
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

// ---------------------------------------------------------------------------
// T022 / T052 – Pin / Unpin mutation hooks
// ---------------------------------------------------------------------------

/** Mutation hook to pin a pipeline for the current user. */
export function usePinPipeline() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (pipelineId: string) => devagentApi.pinPipeline(pipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

/** Mutation hook to unpin a pipeline for the current user. */
export function useUnpinPipeline() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (pipelineId: string) => devagentApi.unpinPipeline(pipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

/**
 * Hook to fetch list of pipelines with optional filtering.
 * T023: Uses adaptive polling based on visibility + activity.
 * T026: Tracks changedPipelineIds by diffing previous vs current data.
 */
export function useDevAgentPipelines(
  filters?: PipelineListFilters,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  const interval = useAdaptiveRefetchInterval();
  const enabled = options?.enabled ?? true;
  const prevPipelinesRef = React.useRef<Map<string, Pipeline>>(new Map());
  const [changedPipelineIds, setChangedPipelineIds] = React.useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: devagentKeys.pipelinesList(filters),
    queryFn: () => devagentApi.listPipelines(filters),
    enabled,
    placeholderData: (previousData) => previousData,
    refetchInterval: enabled ? (options?.refetchInterval ?? interval) : false,
    staleTime: 10_000,
    // Treat HTTP 304 "Not Modified" as a non-error: keep previous data
    retry: (failureCount, error) => {
      if (error instanceof devagentApi.DevAgentApiError && error.status === 304) return false;
      if (!isPipelineDetailRetryableError(error)) return false;
      return failureCount < LIST_QUERY_MAX_RETRIES;
    },
  });

  // T026 – diff previous vs current pipeline data to track changes
  React.useEffect(() => {
    if (!enabled) return;
    const pipelines: Pipeline[] = query.data?.pipelines ?? [];
    const prev = prevPipelinesRef.current;
    const changed = new Set<string>();

    for (const p of pipelines) {
      const prevPipeline = prev.get(p.pipeline_id);
      if (
        prevPipeline &&
        (prevPipeline.status !== p.status ||
          prevPipeline.current_stage !== p.current_stage ||
          prevPipeline.updated_at !== p.updated_at)
      ) {
        changed.add(p.pipeline_id);
      }
    }

    // Update ref for next poll
    const next = new Map<string, Pipeline>();
    for (const p of pipelines) next.set(p.pipeline_id, p);
    prevPipelinesRef.current = next;

    if (changed.size > 0) setChangedPipelineIds(changed);
  }, [enabled, query.data]);

  return { ...query, changedPipelineIds };
}

/**
 * Hook to fetch a single pipeline detail
 */
export function useDevAgentPipeline(pipelineId: string) {
  const interval = useAdaptiveRefetchInterval(DETAIL_POLL_ACTIVE_MS);
  return useQuery({
    queryKey: devagentKeys.pipeline(pipelineId),
    queryFn: () => devagentApi.getPipeline(pipelineId),
    enabled: !!pipelineId,
    refetchInterval: (query) => pipelineDetailRefetchInterval(query.state.data, interval),
    staleTime: 2000,
    retry: (failureCount, error) =>
      isPipelineDetailRetryableError(error) && failureCount < DETAIL_QUERY_MAX_RETRIES,
    retryDelay: pipelineDetailRetryDelay,
  });
}

export function usePipelineDocuments(
  pipelineId: string,
  options?: { enabled?: boolean; updatedAt?: string },
) {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (pipelineId && options?.updatedAt && options?.enabled !== false) {
      // Gold publication can advance the package while its lifecycle remains
      // approval_pending, so generation-only polling cannot detect it.
      void queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineDocuments(pipelineId) });
    }
  }, [pipelineId, options?.updatedAt, options?.enabled, queryClient]);
  return useQuery({
    queryKey: devagentKeys.pipelineDocuments(pipelineId),
    queryFn: async () => {
      const [metadataResult, listingResult] = await Promise.allSettled([
        devagentApi.getPipelineDocumentMetadata(pipelineId),
        devagentApi.listPipelineDocuments(pipelineId),
      ]);
      const metadata = metadataResult.status === 'fulfilled' ? metadataResult.value : null;
      if (listingResult.status === 'rejected' && metadata?.status === 'generating') {
        return { metadata, listing: null };
      }
      if (metadataResult.status === 'rejected') {
        throw metadataResult.reason;
      }
      if (listingResult.status === 'rejected') {
        throw listingResult.reason;
      }
      const listing = listingResult.value;
      if (!metadata && !listing) {
        return null;
      }
      return { metadata, listing };
    },
    enabled: !!pipelineId && (options?.enabled ?? true),
    refetchInterval: (query) => pipelineDocumentsRefetchInterval(query.state.data),
    refetchOnMount: 'always',
    staleTime: 5000,
    retry: false,
  });
}

export function usePipelineDocumentContent(
  pipelineId: string,
  documentName: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...devagentKeys.pipelineDocuments(pipelineId), 'content', documentName],
    queryFn: () => devagentApi.getPipelineDocumentContent(pipelineId, documentName),
    enabled: !!pipelineId && !!documentName && (options?.enabled ?? true),
    staleTime: 5_000,
  });
}

export function useApprovePipelineDocuments() {
  const queryClient = useQueryClient();

  return useMutation<
    ApproveDocumentPackageResponse,
    Error,
    { pipelineId: string; request: ApproveDocumentPackageRequest }
  >({
    mutationFn: ({ pipelineId, request }) => devagentApi.approvePipelineDocuments(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineDocuments(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useRemovePipelineRepository() {
  const queryClient = useQueryClient();

  return useMutation<
    RemoveRepositoryResponse,
    Error,
    { pipelineId: string; request: RemoveRepositoryRequest }
  >({
    mutationFn: ({ pipelineId, request }) => devagentApi.removePipelineRepository(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineDocuments(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useChangePipelineRepositories() {
  const queryClient = useQueryClient();

  return useMutation<
    ChangeRepositoriesResponse,
    Error,
    { pipelineId: string; request: ChangeRepositoriesRequest }
  >({
    mutationFn: ({ pipelineId, request }) => devagentApi.changePipelineRepositories(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineDocuments(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

/**
 * Hook to fetch dashboard statistics
 */
export function useDevAgentStats() {
  return useQuery({
    queryKey: devagentKeys.stats(),
    queryFn: () => devagentApi.getDashboardStats(),
    refetchInterval: 10000, // Refetch every 10 seconds
    staleTime: 5000,
  });
}

export function useDevAgentAnalytics(filters?: devagentApi.AnalyticsFilters) {
  return useQuery({
    queryKey: devagentKeys.analytics(filters),
    queryFn: () => devagentApi.getDevAgentAnalyticsDashboard(filters),
    refetchInterval: 60000,
    staleTime: 30000,
  });
}

export function useRepoSelectionAccuracy(range: AnalyticsDateRange | AnalyticsTimeRange) {
  return useQuery({
    queryKey: devagentKeys.repoSelectionAccuracy(range),
    queryFn: () => devagentApi.getRepoSelectionAccuracy(range),
    staleTime: 60000,
  });
}

export function useRepoOverSelection(range: AnalyticsDateRange | AnalyticsTimeRange) {
  return useQuery({
    queryKey: devagentKeys.repoOverSelection(range),
    queryFn: () => devagentApi.getRepoOverSelection(range),
    staleTime: 60000,
  });
}

/**
 * Hook to create a new pipeline
 */
export function useCreatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { 
      request: CreatePipelineRequest; 
      options?: {
        force?: boolean;
        branch_action?: 'recreate' | 'reuse';
        signal?: AbortSignal;
      }
    }) => devagentApi.createPipeline(params.request, params.options),
    onSuccess: () => {
      // Invalidate and refetch pipelines list
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

/**
 * Hook to approve a pipeline stage
 */
export function useApprovePipelineStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stage, request }: { pipelineId: string; stage: string; request?: ApproveStageRequest }) =>
      devagentApi.approvePipelineStage(pipelineId, stage, request),
    onSuccess: (_data: unknown, variables: { pipelineId: string; stage: string; request?: ApproveStageRequest }) => {
      // Invalidate the specific pipeline to refresh its data
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useAssignDeveloper() {
  const queryClient = useQueryClient();

  return useMutation<AssignDeveloperResponse, Error, { pipelineId: string; request: AssignDeveloperRequest }>({
    mutationFn: ({ pipelineId, request }: { pipelineId: string; request: AssignDeveloperRequest }) =>
      devagentApi.assignDeveloper(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

export function useStartImplementation() {
  const queryClient = useQueryClient();

  return useMutation<StartImplementationResponse, Error, { pipelineId: string; runnerType?: string; runnerModel?: string }>({
    mutationFn: ({ pipelineId, runnerType, runnerModel }) => {
      const request: { runner_type?: string; runner_model?: string } = {};
      if (runnerType) request.runner_type = runnerType;
      if (runnerModel) request.runner_model = runnerModel;
      return devagentApi.startImplementation(pipelineId, request);
    },
    onSuccess: (_data, variables) => {
      const pipelineId = variables.pipelineId;
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useRepositoryWorkspace(
  pipelineId: string,
  repositoryId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: devagentKeys.repositoryWorkspace(pipelineId, repositoryId || 0),
    queryFn: () => devagentApi.getRepositoryWorkspace(pipelineId, repositoryId as number),
    enabled: enabled && !!pipelineId && repositoryId !== null,
    refetchInterval: (query) => {
      const workspace = query.state.data as RepositoryWorkspaceResponse | undefined;
      const hasActiveJob = (workspace?.jobs || []).some(job =>
        ['queued', 'pending', 'running', 'in_progress'].includes(
          String(job.status || '').toLowerCase()
        )
      );
      if (hasActiveJob) return 3_000;
      const monitors = workspace?.pr_monitors || [];
      if (
        monitors.length > 0
        && monitors.every(monitor =>
          !Boolean(monitor.is_active)
          && isPrMonitoringElapsedReason(monitor.blocked_reason, monitor.last_error)
        )
      ) {
        return false;
      }
      return 3_000;
    },
    staleTime: 2_000,
  });
}

export function useRepositoryCheckpointDiff(
  pipelineId: string,
  repositoryId: number | null,
  checkpointId?: string,
) {
  return useQuery({
    queryKey: [...devagentKeys.repositoryWorkspace(pipelineId, repositoryId || 0), 'diff', checkpointId],
    queryFn: () => devagentApi.getRepositoryCheckpointDiff(pipelineId, repositoryId as number, checkpointId),
    enabled: !!pipelineId && repositoryId !== null && !!checkpointId,
    staleTime: Infinity,
  });
}

export function useRepositoryCheckpointLogs(
  pipelineId: string,
  repositoryId: number | null,
  checkpointId?: string,
) {
  return useQuery({
    queryKey: [...devagentKeys.repositoryWorkspace(pipelineId, repositoryId || 0), 'logs', checkpointId],
    queryFn: () => devagentApi.getRepositoryCheckpointLogs(pipelineId, repositoryId as number, checkpointId),
    enabled: !!pipelineId && repositoryId !== null && !!checkpointId,
    staleTime: Infinity,
  });
}

export function usePipelineAskHistory(pipelineId: string) {
  return useQuery({
    queryKey: [...devagentKeys.pipelineAsk(pipelineId), 'history'] as const,
    queryFn: () => devagentApi.getPipelineAskHistory(pipelineId),
    enabled: !!pipelineId,
    staleTime: 5000,
  });
}

export function useSendPipelineAsk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      pipelineId: string;
      question: string;
      model?: string;
      userRequestedLatest?: boolean;
      packageVersion?: number;
      packageCommitSha?: string;
      documentName?: string;
      onEvent?: (event: devagentApi.PipelineAskEvent) => void;
      investigationAttemptId?: string;
      investigationReportSha256?: string;
      intent?: 'auto' | 'question';
      attachments?: devagentApi.PipelineAskAttachment[];
      signal?: AbortSignal;
    }) => {
      const request = {
      question: variables.question,
      model: variables.model,
      user_requested_latest: variables.userRequestedLatest,
      ...(variables.packageVersion ? { package_version: variables.packageVersion } : {}),
      ...(variables.packageCommitSha ? { package_commit_sha: variables.packageCommitSha } : {}),
      ...(variables.documentName ? { document_name: variables.documentName } : {}),
      ...(variables.investigationAttemptId ? { investigation_attempt_id: variables.investigationAttemptId } : {}),
      ...(variables.investigationReportSha256 ? { investigation_report_sha256: variables.investigationReportSha256 } : {}),
      ...(variables.intent ? { intent: variables.intent } : {}),
      ...(variables.attachments?.length ? { attachments: variables.attachments } : {}),
      };
      return variables.onEvent
        ? devagentApi.progressivePipelineAsk(variables.pipelineId, request, variables.onEvent, variables.signal)
        : devagentApi.askPipeline(variables.pipelineId, request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineAsk(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
    },
  });
}

export function useSendRepositoryImplementationMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      pipelineId: string;
      repositoryId: number;
      messageType: 'question' | 'change_request' | 'scope_change';
      runnerModel?: string;
      content: string;
      contextType?: 'implementation' | 'document' | 'pr_guardian';
      documentName?: string;
      guardianContext?: devagentApi.PrGuardianAskContext;
    }) => devagentApi.sendRepositoryImplementationMessage(
      variables.pipelineId,
      variables.repositoryId,
      {
        message_type: variables.messageType,
        runner_model: variables.runnerModel,
        content: variables.content,
        context_type: variables.contextType,
        document_name: variables.documentName,
        guardian_context: variables.guardianContext,
      },
    ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.repositoryWorkspace(variables.pipelineId, variables.repositoryId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelineDocuments(variables.pipelineId) });
    },
  });
}

export function useRetryRepositoryImplementation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { pipelineId: string; repositoryId: number }) =>
      devagentApi.retryRepositoryImplementation(variables.pipelineId, variables.repositoryId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.repositoryWorkspace(variables.pipelineId, variables.repositoryId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
    },
  });
}

export function useRetryAllFailedRepositoryImplementations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pipelineId: string) =>
      devagentApi.retryAllFailedRepositoryImplementations(pipelineId),
    onSuccess: (_data, pipelineId) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(pipelineId) });
    },
  });
}

export function useCancelRepositoryOrchestration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pipelineId: string) => devagentApi.cancelRepositoryOrchestration(pipelineId),
    onSuccess: (_data, pipelineId) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(pipelineId) });
    },
  });
}

export function useConfirmManualImplementation() {
  const queryClient = useQueryClient();

  return useMutation<ConfirmManualImplementationResponse, Error, string>({
    mutationFn: (pipelineId: string) => devagentApi.confirmManualImplementation(pipelineId),
    onSuccess: (_data, pipelineId) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useResolveRequirementGap() {
  const queryClient = useQueryClient();

  return useMutation<
    ResolveRequirementGapResponse,
    Error,
    { pipelineId: string; request: ResolveRequirementGapRequest }
  >({
    mutationFn: ({ pipelineId, request }) =>
      devagentApi.resolveRequirementGap(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

/**
 * Hook to retry a failed pipeline
 */
export function useRetryPipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pipelineId,
      prOnly = false,
      testOnly = false,
      runnerType,
      runnerModel,
      engineeringAuthoringWorkflow,
    }: {
      pipelineId: string;
      prOnly?: boolean;
      testOnly?: boolean;
      runnerType?: string;
      runnerModel?: string;
      engineeringAuthoringWorkflow?: 'cursor_agent_v1';
    }) =>
      engineeringAuthoringWorkflow
        ? devagentApi.retryPipeline(pipelineId, prOnly, testOnly, runnerType, runnerModel, engineeringAuthoringWorkflow)
        : devagentApi.retryPipeline(pipelineId, prOnly, testOnly, runnerType, runnerModel),
    onMutate: async (variables: { pipelineId: string; prOnly?: boolean; testOnly?: boolean; runnerType?: string; runnerModel?: string }) => {
      await queryClient.cancelQueries({ queryKey: devagentKeys.pipelines() });
      const previousLists = queryClient.getQueriesData<ListPipelinesResponse>({
        queryKey: devagentKeys.pipelines(),
      });
      queryClient.setQueriesData<ListPipelinesResponse>(
        { queryKey: devagentKeys.pipelines() },
        (current) => {
          if (!current || !Array.isArray(current.pipelines)) return current;
          return {
            ...current,
            pipelines: current.pipelines.map((pipeline) =>
              pipeline.pipeline_id === variables.pipelineId
                ? {
                    ...pipeline,
                    status: 'in_progress',
                    work_state: 'in_flight',
                    action_label: 'Retrying',
                    has_changes_since_last_seen: true,
                  }
                : pipeline,
            ),
          };
        },
      );
      return { previousLists };
    },
    onError: (_error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      context?.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (_data: unknown, variables: { pipelineId: string; prOnly?: boolean; testOnly?: boolean; runnerType?: string; runnerModel?: string }) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useRecoverDocumentWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, expectedUpdatedAt }: { pipelineId: string; expectedUpdatedAt: string }) =>
      devagentApi.recoverDocumentWorkflow(pipelineId, expectedUpdatedAt),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(variables.pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
    },
  });
}

/**
 * Hook to cancel a running pipeline
 */
export function useCancelPipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pipelineId: string) => devagentApi.cancelPipeline(pipelineId),
    onMutate: async (pipelineId: string) => {
      await queryClient.cancelQueries({ queryKey: devagentKeys.pipelines() });
      const previousLists = queryClient.getQueriesData<ListPipelinesResponse>({
        queryKey: devagentKeys.pipelines(),
      });
      queryClient.setQueriesData<ListPipelinesResponse>(
        { queryKey: devagentKeys.pipelines() },
        (current) => {
          if (!current || !Array.isArray(current.pipelines)) return current;
          return {
            ...current,
            pipelines: current.pipelines.map((pipeline) =>
              pipeline.pipeline_id === pipelineId
                ? {
                    ...pipeline,
                    status: 'cancelled',
                    work_state: 'completed',
                    action_required_by: 'none',
                    action_label: 'Cancelled',
                    has_changes_since_last_seen: true,
                  }
                : pipeline,
            ),
          };
        },
      );
      return { previousLists };
    },
    onError: (_error, _pipelineId, context) => {
      context?.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (_data: unknown, pipelineId: string) => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipeline(pipelineId) });
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      pipelineId: string;
      delete_branch?: boolean;
      cancel_running?: boolean;
      cleanup_worktree?: boolean;
      delete_local_branch?: boolean;
      delete_remote_branch?: boolean;
      abandon_pr?: boolean;
    }) =>
      devagentApi.deletePipeline(params.pipelineId, {
        delete_branch: params.delete_branch,
        cancel_running: params.cancel_running,
        cleanup_worktree: params.cleanup_worktree,
        delete_local_branch: params.delete_local_branch,
        delete_remote_branch: params.delete_remote_branch,
        abandon_pr: params.abandon_pr,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devagentKeys.pipelines() });
      queryClient.invalidateQueries({ queryKey: devagentKeys.stats() });
    },
  });
}

/**
 * Hook to check PR merge status for a pipeline
 * Polls every 10 seconds when enabled
 */
export function usePRStatus(pipelineId: string, enabled: boolean = false) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-status'],
    queryFn: () => devagentApi.getPRStatus(pipelineId),
    enabled: enabled && !!pipelineId,
    refetchInterval: enabled ? 10000 : false, // Poll every 10 seconds when enabled
    staleTime: 5000,
  });
}

/**
 * Hook to fetch PR comments for a pipeline
 * Polls every 30 seconds when enabled
 */
export function usePRComments(
  pipelineId: string,
  enabled: boolean = false,
  pipelineRepositoryId?: number | null,
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-comments', pipelineRepositoryId ?? null],
    queryFn: () => devagentApi.getPRComments(pipelineId, pipelineRepositoryId),
    enabled: enabled && !!pipelineId,
    refetchInterval: enabled ? 30000 : false, // Poll every 30 seconds
    staleTime: 15000,
  });
}

/**
 * Hook to handle a PR comment (fix, reply, ignore)
 */
export function useHandlePRComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      pipelineId, 
      threadId, 
      request 
    }: { 
      pipelineId: string; 
      threadId: number; 
      request: devagentApi.PRCommentActionRequest 
    }) => devagentApi.handlePRComment(pipelineId, threadId, request),
    onSuccess: (_data: unknown, variables: { pipelineId: string; threadId: number; request: devagentApi.PRCommentActionRequest }) => {
      // Invalidate PR comments and pending changes to refresh
      queryClient.invalidateQueries({ 
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-comments'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pending-changes'] 
      });
    },
  });
}

export function useFixAllUnresolved() {
  const queryClient = useQueryClient();

  return useMutation<
    devagentApi.FixAllUnresolvedResponse,
    Error,
    { pipelineId: string; request?: devagentApi.FixAllUnresolvedRequest }
  >({
    mutationFn: ({ pipelineId, request }: { pipelineId: string; request?: devagentApi.FixAllUnresolvedRequest }) =>
      devagentApi.fixAllUnresolvedComments(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-comments'],
      });
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'repository-workspace'],
      });
    },
  });
}

export function usePrMonitor(pipelineId: string, enabled: boolean = false) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-monitor'],
    queryFn: () => devagentApi.getPrMonitor(pipelineId),
    enabled: enabled && !!pipelineId,
    refetchInterval: (query) => {
      if (!enabled) return false;
      const monitor = query.state.data;
      if (
        monitor
        && !monitor.is_active
        && isPrMonitoringElapsedReason(monitor.blocked_reason, monitor.last_error)
      ) {
        return false;
      }
      return 10000;
    },
    staleTime: 5000,
  });
}

export function useRepairActivityLive(
  pipelineId: string,
  enabled: boolean = false,
  pipelineRepositoryId?: number | null,
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'repair-activity-live', pipelineRepositoryId ?? null],
    queryFn: () => devagentApi.getRepairActivityLive(pipelineId, pipelineRepositoryId),
    enabled: enabled && !!pipelineId,
    refetchInterval: enabled ? 3000 : false,
    staleTime: 1000,
  });
}

export function cursorTranscriptRefetchInterval(
  enabled: boolean,
  pollWhileActive: boolean,
  status?: string
): number | false {
  return enabled && (pollWhileActive || status === 'live') ? 2000 : false;
}

export function useCursorTranscriptLive(
  pipelineId: string,
  enabled: boolean = false,
  pollWhileActive: boolean = false,
  pipelineRepositoryId?: number | null,
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'cursor-transcript-live', pipelineRepositoryId ?? null],
    queryFn: () => devagentApi.getCursorTranscriptLive(pipelineId, pipelineRepositoryId),
    enabled: enabled && !!pipelineId,
    refetchInterval: (query) => cursorTranscriptRefetchInterval(
      enabled,
      pollWhileActive,
      query.state.data?.status
    ),
    staleTime: 500,
  });
}

export function useContainerJobLogs(
  pipelineId: string,
  containerId: string,
  enabled: boolean = false,
  polling: boolean = false
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'container-logs', containerId],
    queryFn: () => devagentApi.getContainerJobLogs(pipelineId, containerId),
    enabled: enabled && !!pipelineId && !!containerId,
    refetchInterval: polling ? 3000 : false,
    staleTime: 1000,
  });
}

export function usePipelineTestLogs(
  pipelineId: string,
  enabled: boolean = false,
  polling: boolean = false
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'test-logs'],
    queryFn: () => devagentApi.listTestLogs(pipelineId),
    enabled: enabled && !!pipelineId,
    refetchInterval: polling ? 5000 : false,
    staleTime: 2000,
  });
}

export function usePausePrMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      reason,
    }: {
      pipelineId: string;
      reason?: string;
    }) => devagentApi.pausePrMonitor(pipelineId, { reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-monitor'],
      });
    },
  });
}

export function useResumePrMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      request = {},
    }: {
      pipelineId: string;
      request?: devagentApi.PrMonitorControlRequest;
    }) => devagentApi.resumePrMonitor(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-monitor'],
      });
    },
  });
}

export function useUpdatePrMonitorRunner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      request = {},
    }: {
      pipelineId: string;
      request?: devagentApi.PrMonitorControlRequest;
    }) => devagentApi.updatePrMonitorRunner(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-monitor'],
      });
    },
  });
}

export function useReconcilePrMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pipelineId: string) => devagentApi.reconcilePrMonitor(pipelineId),
    onSuccess: (_data, pipelineId) => {
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-monitor'],
      });
    },
  });
}

export function useRefreshPrMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      request = {},
    }: {
      pipelineId: string;
      request?: devagentApi.PrMonitorControlRequest;
    }) => devagentApi.refreshPrMonitor(pipelineId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: devagentKeys.pipeline(variables.pipelineId),
      });
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pr-monitor'],
      });
      queryClient.invalidateQueries({
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'repository-workspace'],
      });
    },
  });
}

/**
 * Hook to fetch pending changes for a pipeline
 * Polls every 10 seconds when enabled
 */
export function usePendingChanges(pipelineId: string, enabled: boolean = false) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'pending-changes'],
    queryFn: () => devagentApi.getPendingChanges(pipelineId),
    enabled: enabled && !!pipelineId,
    refetchInterval: enabled ? 10000 : false, // Poll every 10 seconds
    staleTime: 5000,
  });
}

/**
 * Hook to start the async commit-fixes task
 */
export function useCommitFixes() {
  return useMutation({
    mutationFn: ({ pipelineId, skipTests = false }: { pipelineId: string; skipTests?: boolean }) => 
      devagentApi.commitPendingFixes(pipelineId, { skip_tests: skipTests }),
  });
}

/**
 * Hook to poll commit-status at an interval.
 * Enable polling by setting `enabled: true` and pass the pipelineId.
 * Automatically stops polling when the task reaches a terminal state.
 */
export function useCommitStatus(pipelineId: string, enabled: boolean) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'commit-status'],
    queryFn: () => devagentApi.getCommitStatus(pipelineId),
    enabled,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      // Stop polling once terminal
      if (state === 'completed' || state === 'failed' || state === 'idle') return false;
      return 5000; // poll every 5 seconds
    },
    // Invalidate related queries when commit completes
    select: (data) => {
      if (data.state === 'completed') {
        queryClient.invalidateQueries({
          queryKey: [...devagentKeys.pipeline(pipelineId), 'pending-changes'],
        });
        queryClient.invalidateQueries({
          queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-comments'],
        });
      }
      return data;
    },
  });
}

/**
 * Hook to poll single-pass PR fix status at an interval.
 */
/** Maximum time (ms) to poll a running fix job before treating it as timed-out. */
const FIX_STATUS_POLL_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes

export function useFixStatus(pipelineId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  // Track when polling started so we can enforce a client-side timeout.
  const pollStartRef = React.useRef<number | null>(null);

  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'fix-status'],
    queryFn: () => devagentApi.getFixStatus(pipelineId),
    enabled,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === 'completed' || state === 'failed' || state === 'idle') {
        pollStartRef.current = null;
        return false;
      }
      if (state === 'running') {
        const now = Date.now();
        if (pollStartRef.current === null) pollStartRef.current = now;
        if (now - pollStartRef.current > FIX_STATUS_POLL_TIMEOUT_MS) {
          // Stop polling after timeout; the useEffect in the page will handle the stuck state.
          return false;
        }
      }
      return 3000;
    },
    select: (data) => {
      if (data.state === 'completed') {
        queryClient.invalidateQueries({
          queryKey: [...devagentKeys.pipeline(pipelineId), 'pr-comments'],
        });
      }
      return data;
    },
  });
}

/**
 * Hook to discard pending PR fixes
 */
export function useDiscardFixes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pipelineId: string) => devagentApi.discardPendingFixes(pipelineId),
    onSuccess: (_data: unknown, pipelineId: string) => {
      // Invalidate pending changes
      queryClient.invalidateQueries({ 
        queryKey: [...devagentKeys.pipeline(pipelineId), 'pending-changes'] 
      });
    },
  });
}

/**
 * Hook to get CI/build failures for a pipeline
 */
export function useCIFailures(
  pipelineId: string,
  enabled: boolean = false,
  pipelineRepositoryId?: number,
) {
  return useQuery({
    queryKey: [...devagentKeys.pipeline(pipelineId), 'ci-failures', pipelineRepositoryId ?? null],
    queryFn: () => devagentApi.getCIFailures(pipelineId, pipelineRepositoryId),
    enabled,
    refetchInterval: 60000, // Poll every minute when enabled
    staleTime: 30000,
  });
}

/**
 * Hook to fix CI failures with AI
 */
export function useFixCIFailures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, request }: { pipelineId: string; request?: devagentApi.CIFixRequest }) => 
      devagentApi.fixCIFailures(pipelineId, request),
    onSuccess: (_data: unknown, variables: { pipelineId: string }) => {
      // Invalidate CI failures and pending changes
      queryClient.invalidateQueries({ 
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'ci-failures'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...devagentKeys.pipeline(variables.pipelineId), 'pending-changes'] 
      });
    },
  });
}
